import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

/**
 * Per-page first-visit explainer modal.
 *
 * Renders a center-screen modal (over a dim backdrop) the first time the
 * signed-in user lands on a page. Tracks dismissal in localStorage keyed by
 * `equia_intro_seen_{userId}_{pageKey}` so it survives reloads but stays
 * scoped to that specific user (a different account on the same browser
 * gets its own tour).
 *
 * Three ways to dismiss: "Got it" button, the X icon, or clicking the
 * backdrop. All persist the flag.
 *
 * Props:
 *   pageKey  — stable string, e.g. "portfolio". Drives the storage key.
 *   title    — short headline ("Welcome to your Portfolio")
 *   subtitle — 1-line context
 *   bullets  — array of { icon, label, text } describing what the user can do
 */
/**
 * Per-page first-visit walkthrough modal.
 *
 * Visibility rules (in order):
 *   1. We need a logged-in user with a known id.
 *   2. Per-page localStorage flag prevents re-showing — `equia_intro_seen_{userId}_{pageKey}`.
 *      Scoped to user+page so each feature page fires its tour **once per
 *      user per browser**. A different account on the same browser, or the
 *      same account on a different device, will see the tour fresh.
 *
 * Intentionally NOT gated on `user.has_seen_onboarding` — that flag flips
 * to `true` as soon as the Dashboard "Add asset" walkthrough finishes, and
 * tying the per-page intros to it would silently suppress every other
 * page's first-visit tour (the bug a beta tester reported Feb 28, 2026).
 *
 * Three ways to dismiss: "Got it" button, the X icon, or clicking the
 * backdrop. All persist the flag.
 *
 * Storage key: `equia_intro_seen_{userId}_{pageKey}`.
 */
export default function PageIntro({ pageKey, title, subtitle, bullets = [] }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  const userId = user?.id || user?._id;
  const storageKey = userId ? `equia_intro_seen_${userId}_${pageKey}` : null;

  useEffect(() => {
    if (!storageKey) return;
    let seen = false;
    try {
      seen = localStorage.getItem(storageKey) === "1";
    } catch {
      seen = false;
    }
    if (seen) return;
    // Slight delay so the modal follows the page entrance animation.
    const t = setTimeout(() => setOpen(true), 500);
    return () => clearTimeout(t);
  }, [storageKey]);

  // Lock body scroll while the modal is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Allow Escape to dismiss
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") dismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const dismiss = () => {
    setOpen(false);
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, "1");
    } catch {
      /* private mode — ignore */
    }
  };

  if (!storageKey) return null;

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/85 backdrop-blur-sm z-[60]"
            onClick={dismiss}
            data-testid={`page-intro-backdrop-${pageKey}`}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ type: "spring", damping: 26, stiffness: 280 }}
            className="fixed inset-0 z-[61] flex items-center justify-center px-4 py-6 pointer-events-none"
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby={`page-intro-title-${pageKey}`}
              className="pointer-events-auto w-full max-w-[560px] max-h-[85vh] overflow-y-auto bg-[#0A0A0A] border border-[#D4AF37]/30 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.9)]"
              data-testid={`page-intro-${pageKey}`}
            >
              {/* Close */}
              <button
                onClick={dismiss}
                data-testid={`page-intro-close-${pageKey}`}
                aria-label="Dismiss intro"
                className="absolute top-4 right-4 md:top-5 md:right-5 text-white/60 hover:text-white transition-colors z-10 p-2"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Header */}
              <div className="px-8 md:px-10 pt-10 md:pt-12 pb-2 relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.4} />
                  </div>
                  <span className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">
                    First Visit · Quick Tour
                  </span>
                </div>
                <h2
                  id={`page-intro-title-${pageKey}`}
                  className="font-serif text-2xl md:text-3xl text-white tracking-tight leading-tight"
                >
                  {title}
                </h2>
                {subtitle && (
                  <p className="text-white/80 text-sm mt-3 leading-relaxed max-w-[480px]">
                    {subtitle}
                  </p>
                )}
              </div>

              {/* Bullets */}
              {bullets.length > 0 && (
                <div className="px-8 md:px-10 py-7 space-y-5">
                  {bullets.map((b, i) => {
                    const Icon = b.icon;
                    return (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.15 + i * 0.07 }}
                        className="flex items-start gap-4"
                      >
                        {Icon && (
                          <div className="w-9 h-9 bg-white/[0.03] border border-white/[0.10] flex items-center justify-center flex-shrink-0">
                            <Icon className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0 pt-0.5">
                          {b.label && (
                            <p className="text-white text-sm font-medium leading-snug">{b.label}</p>
                          )}
                          {b.text && (
                            <p className="text-white/80 text-xs md:text-[13px] leading-relaxed mt-1.5">
                              {b.text}
                            </p>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}

              {/* Footer */}
              <div className="px-8 md:px-10 py-5 border-t border-white/[0.10] flex items-center justify-between gap-4 bg-[#070707]">
                <span className="text-white/55 text-[10px] tracking-[0.2em] uppercase">
                  Shown only once
                </span>
                <button
                  onClick={dismiss}
                  data-testid={`page-intro-got-it-${pageKey}`}
                  className="bg-[#D4AF37] hover:bg-[#E8C757] text-black text-xs font-semibold tracking-[0.15em] uppercase px-6 py-2.5 transition-colors"
                >
                  Got it
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
